import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    Skeleton,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    FolderOpen as FolderIcon,
    Delete as DeleteIcon,
    Storage as StorageIcon,
} from '@mui/icons-material';
import { getProjects, createProject, updateProject, deleteProject } from '../services/api';

/**
 * Projects Page
 * 
 * Displays all projects and allows creating new ones.
 * Each project card shows file counts per source system.
 */
function ProjectsPage() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newProject, setNewProject] = useState({ name: '', description: '' });
    const [creating, setCreating] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingProjectId, setEditingProjectId] = useState('');
    const [editProject, setEditProject] = useState({ name: '', description: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    // Fetch projects on mount
    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const data = await getProjects();
            setProjects(data.projects || []);
            setError(null);
        } catch (err) {
            setError('Failed to load projects. Please ensure the backend is running.');
            console.error('Error fetching projects:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProject = async () => {
        if (!newProject.name.trim()) return;

        try {
            setCreating(true);
            const project = await createProject(newProject);
            setProjects([project, ...projects]);
            setDialogOpen(false);
            setNewProject({ name: '', description: '' });
        } catch (err) {
            setError('Failed to create project. Please try again.');
            console.error('Error creating project:', err);
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteProject = async (projectId, e) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
            return;
        }

        try {
            await deleteProject(projectId);
            setProjects(projects.filter(p => p.id !== projectId));
        } catch (err) {
            setError('Failed to delete project. Please try again.');
            console.error('Error deleting project:', err);
        }
    };

    const handleOpenEditDialog = (project, e) => {
        e.stopPropagation();
        setEditingProjectId(project.id);
        setEditProject({
            name: project.name || '',
            description: project.description || '',
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingProjectId('');
        setEditProject({ name: '', description: '' });
    };

    const handleUpdateProject = async () => {
        if (!editingProjectId || !editProject.name.trim()) return;

        try {
            setSavingEdit(true);
            const updatedProject = await updateProject(editingProjectId, {
                name: editProject.name.trim(),
                description: editProject.description.trim(),
            });
            setProjects((currentProjects) => currentProjects.map((project) => (
                project.id === editingProjectId ? updatedProject : project
            )));
            setSavingEdit(false);
            handleCloseEditDialog();
        } catch (err) {
            setError('Failed to update project. Please try again.');
            console.error('Error updating project:', err);
        } finally {
            setSavingEdit(false);
        }
    };

    return (
        <Box>
            {/* Page Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom fontWeight={700} color="text.primary">
                        Projects
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Create and manage schema mapping projects for report migration.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    size="large"
                >
                    New Project
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Projects Grid */}
            <Grid container spacing={3}>
                {loading ? (
                    // Loading skeletons
                    [...Array(3)].map((_, index) => (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                            <Card>
                                <CardContent>
                                    <Skeleton variant="text" width="60%" height={32} />
                                    <Skeleton variant="text" width="80%" />
                                </CardContent>
                                <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                                    <Skeleton variant="text" width={110} />
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                ) : projects.length === 0 ? (
                    // Empty state
                    <Grid item xs={12}>
                        <Card sx={{ textAlign: 'center', py: 6 }}>
                            <StorageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No projects yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Create your first project to start uploading data dictionaries.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setDialogOpen(true)}
                            >
                                Create Project
                            </Button>
                        </Card>
                    </Grid>
                ) : (
                    // Project cards
                    projects.map((project) => (
                        <Grid item xs={12} md={6} lg={4} key={project.id}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 32px rgba(0, 119, 200, 0.15)',
                                        borderColor: 'primary.main',
                                    },
                                }}
                                onClick={() => navigate(`/schema-analyzer/projects/${project.id}`)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <FolderIcon sx={{ color: 'primary.main' }} />
                                            <Typography variant="h6" component="h2" fontWeight={600} color="text.primary">
                                                {project.name}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleOpenEditDialog(project, e)}
                                                sx={{
                                                    color: 'text.secondary',
                                                    '&:hover': { color: 'primary.main' }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleDeleteProject(project.id, e)}
                                                sx={{
                                                    color: 'text.secondary',
                                                    '&:hover': { color: 'error.main' }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    {project.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            {project.description}
                                        </Typography>
                                    )}
                                </CardContent>
                                <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Created: {new Date(project.created_at).toLocaleDateString()}
                                    </Typography>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>

            {/* Create Project Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Create New Project</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Project Name"
                        fullWidth
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        placeholder="e.g., Customer Reports Migration"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Description (optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                        placeholder="Brief description of what this project is for..."
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateProject}
                        variant="contained"
                        disabled={!newProject.name.trim() || creating}
                    >
                        {creating ? 'Creating...' : 'Create Project'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Edit Project</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Project Name"
                        fullWidth
                        value={editProject.name}
                        onChange={(e) => setEditProject({ ...editProject, name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Description (optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={editProject.description}
                        onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseEditDialog} color="inherit" disabled={savingEdit}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateProject}
                        variant="contained"
                        disabled={!editProject.name.trim() || savingEdit}
                    >
                        {savingEdit ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ProjectsPage;
